/**
 * Centralized product image and data config.
 * Add or edit entries here to keep product content and assets organized.
 */

import img1 from '../Images/1.jpg';
import img2 from '../Images/2.jpg';
import img3 from '../Images/3.jpg';

/** Pythograss collection images – one carousel entry per design set */
import bridge1 from '../Images/Pythograss/bridge 1.jpg';
import bridge2 from '../Images/Pythograss/bridge 2.jpg';
import bridge3 from '../Images/Pythograss/bridge 3.jpg';
import bridge4 from '../Images/Pythograss/bridge 4.jpg';
import doubleLoop1 from '../Images/Pythograss/double loop 1.jpg';
import doubleLoop2 from '../Images/Pythograss/double loop 2.jpg';
import eiffelTower1 from '../Images/Pythograss/eiffle tower 1.jpg';
import eiffelTower2 from '../Images/Pythograss/eiffle tower 2.jpg';
import eiffelTower3 from '../Images/Pythograss/eiffle tower 3.jpg';
import loop2 from '../Images/Pythograss/loop 2.jpg';
import loop3 from '../Images/Pythograss/loop 3.jpg';
import loop4 from '../Images/Pythograss/loop 4.jpg';
import maze1 from '../Images/Pythograss/maze 1.jpg';
import maze2 from '../Images/Pythograss/maze 2.jpg';
import revolve1 from '../Images/Pythograss/revolve 1.jpg';
import revolve2 from '../Images/Pythograss/revolve 2.jpg';
import revolve3 from '../Images/Pythograss/revolve 3.jpg';
import revolve4 from '../Images/Pythograss/revolve 4.jpg';
import volcano1 from '../Images/Pythograss/Volcano 1.jpg';
import volcano2 from '../Images/Pythograss/Volcano 2.jpg';

/** Local images from src (bundled). Replace with your product photos. */
const PRODUCT_IMAGES = {
  img1,
  img2,
  img3,
};

/** Single image entry for carousels */
export interface CarouselImage {
  src: string;
  alt: string;
}

/** Dimensions block for product specs */
export interface ProductDimensions {
  length: string;
  width: string;
  height: string;
}

/** Full product entry for listing/cards */
export interface ProductEntry {
  id: string;
  title: string;
  description: string;
  options: string[];
  dimensions: ProductDimensions;
  images: CarouselImage[];
}

/** Pythograss Collection: one product card + carousel per design (same structure as Products Carousel) */
export const PYTHOGRASS_PRODUCTS: ProductEntry[] = [
  {
    id: 'pythograss-bridge',
    title: 'Suspension Bridge',
    description: 'A delicate arched bridge introduces elevation and drama to the putting path. The ball must travel smoothly across the suspended deck, creating a subtle shift in level that adds both visual excitement and a rewarding moment of precision.',
    options: [],
    dimensions: { length: 'length: 32".', width: 'width: 7".', height: 'height: 15".' },
    images: [
      { src: bridge1, alt: 'Pythograss Bridge - 1' },
      { src: bridge2, alt: 'Pythograss Bridge - 2' },
      { src: bridge3, alt: 'Pythograss Bridge - 3' },
      { src: bridge4, alt: 'Pythograss Bridge - 4' },
    ],
  },
  {
    id: 'pythograss-double-loop',
    title: 'Double Loop',
    description: 'Twin concentric loops create a flowing sculptural tunnel. Players guide the ball through layered curves, blending rhythm and alignment in a sleek contemporary obstacle.',
    options: [],
    dimensions: { length: 'length: 35".', width: 'width: 36".', height: 'height: 36".' },
    images: [
      { src: doubleLoop1, alt: 'Pythograss Double Loop - 1' },
      { src: doubleLoop2, alt: 'Pythograss Double Loop - 2' },
    ],
  },
  {
    id: 'pythograss-eiffel-tower',
    title: 'Eiffel Tower',
    description: 'An iconic architectural centerpiece inspired by Paris, rendered in intricate metal craftsmanship. The putting line passes directly beneath the tower\'s elevated base, demanding accuracy as the ball travels through the structure before reaching the cup.',
    options: [],
    dimensions: { length: 'length: 34".', width: 'width: 34".', height: 'height: 64".' },
    images: [
      { src: eiffelTower1, alt: 'Pythograss Eiffel Tower - 1' },
      { src: eiffelTower2, alt: 'Pythograss Eiffel Tower - 2' },
      { src: eiffelTower3, alt: 'Pythograss Eiffel Tower - 3' },
    ],
  },
  {
    id: 'pythograss-loop',
    title: 'Single Loop',
    description: 'A smooth curved ramp forms a sculptural single loop. The ball accelerates through the arc before emerging toward the target, rewarding controlled speed and confident aim.',
    options: [],
    dimensions: { length: 'length: 30".', width: 'width: 15".', height: 'height: 20".' },
    images: [
      { src: loop2, alt: 'Pythograss Loop - 2' },
      { src: loop3, alt: 'Pythograss Loop - 3' },
      { src: loop4, alt: 'Pythograss Loop - 4' },
    ],
  },
  {
    id: 'pythograss-maze',
    title: 'The Maze',
    description: 'A layered spiral pathway draws players inward toward a deceptive central cup that is intentionally false. The true target lies beyond—once the ball reaches the center, it is carried upward and redirected through a concealed side exit. A playful illusion that rewards curiosity, patience, and precise control.',
    options: [],
    dimensions: { length: 'length: 48"', width: 'width: 44".', height: 'height: 15".' },
    images: [
      { src: maze1, alt: 'Pythograss Maze - 1' },
      { src: maze2, alt: 'Pythograss Maze - 2' },
    ],
  },
  {
    id: 'pythograss-revolve',
    title: 'Revolve Funnel',
    description: 'A tapered circular funnel draws the ball inward in a satisfying spiral descent. Enter the funnel at the correct angle and watch the ball orbit smoothly before settling toward the central cup.',
    options: [],
    dimensions: { length: 'length: 59".', width: 'width: 23".', height: 'height: 15".' },
    images: [
      { src: revolve1, alt: 'Pythograss Revolve - 1' },
      { src: revolve2, alt: 'Pythograss Revolve - 2' },
      { src: revolve3, alt: 'Pythograss Revolve - 3' },
      { src: revolve4, alt: 'Pythograss Revolve - 4' },
    ],
  },
  {
    id: 'pythograss-volcano',
    title: 'Volcano Summit',
    description: 'A bold conical landform rises from the course, drawing both the eye and the putting line upward. Players launch the ball along a central ascent to reach the summit before it disappears into the crater. The volcano acts as a dramatic transition feature combining elevation, anticipation, and reference.',
    options: [],
    dimensions: { length: 'length: 57".', width: 'width: 32".', height: 'height: 21".' },
    images: [
      { src: volcano1, alt: 'Pythograss Volcano - 1' },
      { src: volcano2, alt: 'Pythograss Volcano - 2' },
    ],
  },
];
